import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Typography, Button, Modal, Form, Input, List } from 'antd'
import { PlusOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { FlexSectionHeader } from '../style'
import { STUDENT } from '../../constants/userRoles'
import ModuleList from './components/ModuleList'
import {
  getAllModules,
  createModule,
  updateModule,
  deleteModule,
  createModuleItem,
  deleteModuleItem,
  clearModules
} from '../../reducers/moduleReducer'
import useCoursePrivilege from '../../hooks/useCourseprivilege'

const { confirm } = Modal

const Modules = (props) => {
  const { Title } = Typography
  const [addModalActive, setAddModalActive] = useState(false)
  const [form] = Form.useForm()
  const modules = useSelector((state) => state.modules)
  const { enrolled, privilege } = useCoursePrivilege()
  const [editingModule, setEditingModule] = useState(null)

  const { courseId } = useParams()

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(getAllModules(courseId))

    return () => dispatch(clearModules())
  }, [courseId, dispatch])

  const addModule = (module) => {
    dispatch(createModule(courseId, module))
    setAddModalActive(false)
    form.resetFields()
  }

  const editModule = (moduleId, module) => {
    dispatch(updateModule(courseId, moduleId, module))
    setEditingModule(null)
    form.resetFields()
  }

  const removeModule = (moduleId) => {
    confirm({
      title: 'Are you sure you want to delete this module?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        dispatch(deleteModule(courseId, moduleId))
      },
      onCancel() {
        return
      }
    })
  }

  const addModuleItem = (moduleId, moduleItem) => {
    return dispatch(createModuleItem(courseId, moduleId, moduleItem))
  }

  const removeModuleItem = (moduleId, moduleItem) => {
    confirm({
      title: 'Are you sure you want to delete this item?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        dispatch(deleteModuleItem(courseId, moduleId, moduleItem))
      },
      onCancel() {
        return
      }
    })
  }

  const handleCancel = () => {
    form.resetFields()
    setAddModalActive(false)
  }

  return (
    <React.Fragment>
      <FlexSectionHeader>
        <Title level={3}>Modules</Title>
        {enrolled && privilege !== STUDENT && (
          <Button
            onClick={() => setAddModalActive(true)}
            type="dashed"
            shape="round"
            icon={<PlusOutlined />}
          >
            Add Module
          </Button>
        )}
      </FlexSectionHeader>

      <Modal
        title="Add New Module"
        visible={addModalActive}
        onOk={form.submit}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={form.submit}>
            Submit
          </Button>
        ]}
      >
        <Form
          name="Add module"
          form={form}
          onFinish={addModule}
          requiredMark={false}
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
        >
          <Form.Item
            name="title"
            label="Course module"
            rules={[
              {
                required: true,
                message: 'Please enter the module name'
              }
            ]}
          >
            <Input placeholder="Module name" />
          </Form.Item>
        </Form>
      </Modal>

      <div style={{ marginTop: '16px' }}>
        <List
          dataSource={modules}
          renderItem={(module) => (
            <List.Item style={{ padding: '12px 10px !important' }}>
              <ModuleList
                module={module}
                instructorAccess={privilege !== STUDENT}
                editModule={(updatedModule) =>
                  editModule(module.id, updatedModule)
                }
                removeModule={removeModule}
                addModuleItem={(moduleItem) =>
                  addModuleItem(module.id, moduleItem)
                }
                removeModuleItem={(moduleItem) =>
                  removeModuleItem(module.id, moduleItem)
                }
                editingModule={editingModule}
                setEditingModule={setEditingModule}
              />
            </List.Item>
          )}
        />
      </div>
    </React.Fragment>
  )
}

export default Modules
